import React from 'react';
import { CartesianGrid, Line, LineChart, ReferenceDot, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import type { RetentionPoint } from '../../utils/retention';
import { CHART } from '../../utils/theme';
import { fmtPct } from '../../utils/format';
import { ChartTooltip, LegendItem } from '../ui/ChartTooltip';
interface CohortTickProps {
  x?: number;
  y?: number;
  payload?: {
    value: string;
  };
}

/** Two controlled lines (base season / comparison) so labels never wrap unpredictably. */
function CohortTick({
  x = 0,
  y = 0,
  payload
}: CohortTickProps) {
  const value = payload?.value ?? '';
  const [base, rest] = value.includes(' → ') ? value.split(' → ') : value.split(' +');
  const second = value.includes(' → ') ? `→ ${rest}` : rest ? `+${rest}` : '';
  return <g transform={`translate(${x},${y})`} data-id="element-493" data-mp="18lzc67c7an6k">
      <text textAnchor="middle" fontSize={11} fill={CHART.axis} data-id="element-494" data-mp="18kwkhx720gpl">
        <tspan x={0} dy={12} data-id="element-495" data-mp="13o66rgiq1x7c">{base}</tspan>
        {second && <tspan x={0} dy={13} fill={CHART.muted} data-id="element-496" data-mp="13lk1xon7tvst">{second}</tspan>}
      </text>
    </g>;
}
export function RetentionTrendChart({
  points,
  selectedBase,
  entityLabel
}: {
  points: RetentionPoint[];
  selectedBase: number;
  entityLabel: string;
}) {
  const selected = points.find(p => p.base === selectedBase);
  const axis = {
    tick: {
      fontSize: 11,
      fill: CHART.axis
    },
    axisLine: false,
    tickLine: false
  } as const;
  return <div data-id="element-497" data-mp="246pohf7pznoj">
      <div className="mb-3 flex flex-wrap gap-x-4 gap-y-1" data-id="element-498" data-mp="246zf950l9yva">
        <LegendItem color={CHART.navy} label={entityLabel} line data-id="element-499" data-mp="248ke36txs3yy" />
        <LegendItem color={CHART.accent} label="Network" line dashed data-id="element-500" data-mp="296c23prgn5xv" />
        <LegendItem color={CHART.accent} label="Selected cohort" data-id="element-501" data-mp="2e444jdpqeagb" />
      </div>
      <div className="h-[280px]" data-id="element-502" data-mp="2i68ykdk3d93h">
        <ResponsiveContainer width="100%" height="100%" data-id="element-503" data-mp="2i7a8ufta9rk5">
          <LineChart data={points} margin={{
          top: 10,
          right: 16,
          left: 0,
          bottom: 0
        }} data-id="element-504" data-mp="2i3w8te83dufv">
            <CartesianGrid vertical={false} stroke={CHART.grid} data-id="element-505" data-mp="3plvk8wvivgpj" />
            <XAxis dataKey="label" axisLine={{
            stroke: CHART.grid
          }} tickLine={false} interval={0} height={40} tick={<CohortTick data-id="element-507" data-mp="1uf2uzf4xbfp2" />} data-id="element-506" data-mp="3tk2vjc77vptc" />
            <YAxis tickFormatter={(v: number) => fmtPct(v, 0)} width={44} {...axis} domain={[0, 'auto']} data-id="element-508" data-mp="1uigy4plevsxk" />
            <Tooltip content={<ChartTooltip formatter={v => fmtPct(v)} data-id="element-510" data-mp="23h2gokqvv7yc" />} data-id="element-509" data-mp="23hc543wa571x" />
            <Line type="monotone" dataKey="scope" name={entityLabel} stroke={CHART.navy} strokeWidth={2.25} dot={{
            r: 3,
            fill: CHART.navy
          }} connectNulls isAnimationActive={false} data-id="element-511" data-mp="23f83jslusk4t" />
            <Line type="monotone" dataKey="network" name="Network" stroke={CHART.accent} strokeWidth={2} strokeDasharray="5 4" dot={false} connectNulls isAnimationActive={false} data-id="element-512" data-mp="1at6ifblo17zg" />
            {selected && selected.scope !== null && <ReferenceDot x={selected.label} y={selected.scope} r={6} fill={CHART.accent} stroke="#fff" strokeWidth={2} data-id="element-513" data-mp="1kkjdec2rge9p" />}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>;
}